/* Copy da gestão de documentos legais versionados (ADR-0774). O alerta info mistura texto
   simples com trechos em <strong>/<em>; cada segmento vira campo próprio para a marcação (JSX)
   ficar no componente e só o texto vir da copy — mesmo padrão de `releases.ts`. */
import type { LegalDocKey } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface LegalContent {
  readonly docLabels: Readonly<Record<LegalDocKey, string>>;
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly newVersionButton: string;
  readonly infoVersionNumberIntro: string;
  readonly infoVersionNumberStrong: string;
  readonly infoAfterVersionNumber: string;
  readonly infoPublishedImmutableStrong: string;
  readonly infoAfterPublishedImmutable: string;
  readonly infoEffectiveAtStrong: string;
  readonly infoBetweenEffectiveAndPublished: string;
  readonly infoPublishedAtStrong: string;
  readonly infoAfterPublishedAt: string;
  readonly infoSameVersionEm: string;
  readonly infoAfterSameVersion: string;
  /** `super_admin` é identificador de role do sistema — igual nos dois idiomas. */
  readonly infoSuperAdminStrong: string;
  readonly infoAfterSuperAdmin: string;
  readonly docPickerAriaLabel: string;
  readonly loadError: string;
  readonly removeDraftTitle: string;
  readonly removeDraftDescription: (version: number) => string;
  readonly removeConfirmLabel: string;
  readonly toastDraftRemoved: (version: number) => string;
  readonly toastPublishedNow: (version: number) => string;
  readonly toastPublishedScheduled: (version: number) => string;
  readonly bulk: {
    readonly selected: (count: number) => string;
    readonly remove: string;
    readonly clear: string;
    readonly title: string;
    readonly description: (count: number) => string;
    readonly success: (count: number) => string;
    readonly partial: (success: number, failed: number) => string;
  };
}

const LEGAL_CONTENT: Bilingual<LegalContent> = {
  'pt-BR': {
    docLabels: { terms: 'Termos de uso', privacy: 'Política de privacidade' },
    pageTitle: 'Documentos legais',
    pageDescription:
      'Termos de uso e política de privacidade servidos ao app e ao site, versionados.',
    newVersionButton: 'Nova versão',
    infoVersionNumberIntro: 'O ',
    infoVersionNumberStrong: 'número da versão',
    infoAfterVersionNumber: ' é atribuído pelo servidor (não é campo de formulário). Versão ',
    infoPublishedImmutableStrong: 'publicada é imutável',
    infoAfterPublishedImmutable:
      ': corrigir texto publicado significa criar a versão seguinte a partir dela. ',
    infoEffectiveAtStrong: 'Vigora em',
    infoBetweenEffectiveAndPublished: ' é separado de ',
    infoPublishedAtStrong: 'Publicado em',
    infoAfterPublishedAt:
      ' — dá para publicar hoje com vigência futura (aviso prévio). Cada versão tem uma linha ' +
      'por idioma; idioma faltando cai para pt-BR ',
    infoSameVersionEm: 'dentro da mesma versão',
    infoAfterSameVersion: ', nunca rebaixa a versão. Publicar e remover são restritos a ',
    infoSuperAdminStrong: 'super_admin',
    infoAfterSuperAdmin: ' e ficam na trilha de auditoria.',
    docPickerAriaLabel: 'Escolher documento legal',
    loadError: 'Falha ao carregar as versões deste documento.',
    removeDraftTitle: 'Remover rascunho',
    removeDraftDescription: (version) =>
      `Remover o rascunho da versão ${version} e todos os seus idiomas? Somente rascunhos ` +
      'podem ser removidos — texto que vigorou permanece no histórico.',
    removeConfirmLabel: 'Remover',
    toastDraftRemoved: (version) => `Rascunho da versão ${version} removido.`,
    toastPublishedNow: (version) => `Versão ${version} publicada e em vigor.`,
    toastPublishedScheduled: (version) =>
      `Versão ${version} publicada — vigora na data programada.`,
    bulk: {
      selected: (count) => `${count} rascunho(s) selecionado(s)`,
      remove: 'Remover selecionados',
      clear: 'Limpar seleção',
      title: 'Remover rascunhos selecionados',
      description: (count) =>
        `Remover ${count} rascunho(s) e todos os idiomas? Versões publicadas não entram no lote.`,
      success: (count) => `${count} rascunho(s) removido(s).`,
      partial: (success, failed) => `${success} removido(s); ${failed} com falha.`,
    },
  },
  'en-US': {
    docLabels: { terms: 'Terms of Service', privacy: 'Privacy Policy' },
    pageTitle: 'Legal documents',
    pageDescription:
      'Terms of Service and Privacy Policy served to the app and website, versioned.',
    newVersionButton: 'New version',
    infoVersionNumberIntro: 'The ',
    infoVersionNumberStrong: 'version number',
    infoAfterVersionNumber: ' is assigned by the server (it is not a form field). A ',
    infoPublishedImmutableStrong: 'published version is immutable',
    infoAfterPublishedImmutable:
      ': fixing published text means creating the next version from it. ',
    infoEffectiveAtStrong: 'Effective on',
    infoBetweenEffectiveAndPublished: ' is separate from ',
    infoPublishedAtStrong: 'Published on',
    infoAfterPublishedAt:
      ' — you can publish today with a future effective date (advance notice). Each version ' +
      'has one row per language; a missing language falls back to pt-BR ',
    infoSameVersionEm: 'within the same version',
    infoAfterSameVersion:
      ', never downgrades the version. Publishing and removing are restricted to ',
    infoSuperAdminStrong: 'super_admin',
    infoAfterSuperAdmin: ' and are logged in the audit trail.',
    docPickerAriaLabel: 'Choose legal document',
    loadError: 'Failed to load versions for this document.',
    removeDraftTitle: 'Remove draft',
    removeDraftDescription: (version) =>
      `Remove the draft of version ${version} and all its languages? Only drafts can be ` +
      'removed — text that was ever in effect remains in the history.',
    removeConfirmLabel: 'Remove',
    toastDraftRemoved: (version) => `Draft of version ${version} removed.`,
    toastPublishedNow: (version) => `Version ${version} published and now in effect.`,
    toastPublishedScheduled: (version) =>
      `Version ${version} published — takes effect on the scheduled date.`,
    bulk: {
      selected: (count) => `${count} draft(s) selected`,
      remove: 'Remove selected',
      clear: 'Clear selection',
      title: 'Remove selected drafts',
      description: (count) =>
        `Remove ${count} draft(s) and all languages? Published versions are excluded.`,
      success: (count) => `${count} draft(s) removed.`,
      partial: (success, failed) => `${success} removed; ${failed} failed.`,
    },
  },
};

export const useLegalContent = (): LegalContent => useBilingual(LEGAL_CONTENT);
